import React, { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronUp, Star, X } from "lucide-react";
import type { Drink } from "../../types";
import { drinksApi } from "../../api/drinks";
import { DRINK_MENU } from "../../constants/drinkMenu";
import { UI_TEXT } from "../../constants/uiText";

interface FavoriteDrinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: (items: Drink[]) => void;
}

const FavoriteDrinksModal: React.FC<FavoriteDrinksModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
}) => {
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFavorites = useCallback(async () => {
    setIsLoading(true);
    try {
      const items = await drinksApi.getDrinkFavorites();
      setSelectedIds(new Set(items.map((d) => d.menu_id)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchFavorites();
      if (DRINK_MENU.length > 0) {
        setOpenCategories(new Set([DRINK_MENU[0].id]));
      }
    }
  }, [isOpen, fetchFavorites]);

  if (!isOpen) return null;

  const toggleCategory = (categoryId: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleItem = (drinkItemId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(drinkItemId)) {
        next.delete(drinkItemId);
      } else {
        next.add(drinkItemId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const favDrinks: Drink[] = [];
      DRINK_MENU.forEach((cat) => {
        cat.subCategories.forEach((sub) => {
          sub.items.forEach((item) => {
            if (selectedIds.has(item.id)) {
              favDrinks.push({
                category_id: cat.id,
                category_name: cat.name,
                sub_category_id: sub.id,
                sub_category_name: sub.name,
                menu_id: item.id,
                menu_name: item.name,
                price: item.price,
              });
            }
          });
        });
      });
      await drinksApi.createDrinkFavorite(favDrinks);
      onSaveSuccess(favDrinks);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-white rounded-[28px] overflow-hidden">
      {/* ヘッダー */}
      <div className="bg-slate-900 p-6 text-white relative shrink-0">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
          aria-label={UI_TEXT.favoriteDrinks.close}
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
        <h3 className="text-xl font-black tracking-tighter uppercase text-center">
          {UI_TEXT.favoriteDrinks.title}
        </h3>
        <p className="text-center text-xs text-white/60 mt-1">
          {UI_TEXT.favoriteDrinks.subtitle}
        </p>
      </div>

      {/* コンテンツ */}
      <div className="flex-1 overflow-y-auto p-5 space-y-3 scrollbar-hide">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
            {UI_TEXT.common.loading}
          </div>
        ) : (
          DRINK_MENU.map((category) => {
            const isOpen = openCategories.has(category.id);
            return (
              <div
                key={category.id}
                className="border border-slate-200 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                >
                  <span className="font-bold text-slate-800 text-sm">
                    {category.name}
                  </span>
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  )}
                </button>

                {isOpen && (
                  <div>
                    {category.subCategories.map((sub) => (
                      <div key={sub.id}>
                        {sub.name !== "-" && (
                          <div className="px-4 py-2 bg-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            {sub.name}
                          </div>
                        )}
                        <div className="divide-y divide-slate-100">
                          {sub.items.map((item) => {
                            const isFav = selectedIds.has(item.id);
                            return (
                              <button
                                key={item.id}
                                className="w-full flex items-center justify-between px-4 py-3 text-left active:bg-slate-50 transition-colors"
                                onClick={() => toggleItem(item.id)}
                              >
                                <div>
                                  <p className="font-medium text-slate-800 text-sm">
                                    {item.name}
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    ¥{item.price.toLocaleString()}
                                  </p>
                                </div>
                                <Star
                                  className={`w-5 h-5 shrink-0 transition-colors ${
                                    isFav
                                      ? "text-amber-400 fill-amber-400"
                                      : "text-slate-300"
                                  }`}
                                />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* フッター */}
      <div className="shrink-0 px-5 pb-6 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-slate-500">
            {UI_TEXT.favoriteDrinks.selectedCount(selectedIds.size)}
          </span>
        </div>
        <button
          onClick={handleSave}
          disabled={isSubmitting}
          className="w-full bg-slate-900 text-white font-bold py-3.5 rounded-xl text-sm disabled:opacity-50 transition-opacity active:opacity-80"
        >
          {isSubmitting ? UI_TEXT.common.loading : UI_TEXT.favoriteDrinks.save}
        </button>
      </div>
    </div>
  );
};

export default FavoriteDrinksModal;
